// Typed API client. Single source of truth for access-token injection and
// automatic refresh on 401.

import { useAuthStore } from "@/stores/auth";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  method?: Method;
  body?: unknown;
  // Skip the Authorization header (used by /auth/login, /auth/register, /auth/refresh).
  anonymous?: boolean;
  // Skip the 401 -> refresh -> retry dance (used by /auth/refresh itself).
  noRefresh?: boolean;
  signal?: AbortSignal;
}

const BASE = "/api/v1";

async function rawRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (!opts.anonymous) {
    const token = useAuthStore.getState().accessToken;
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: "include",
    signal: opts.signal,
  });
  if (res.status === 204) {
    return undefined as T;
  }
  const contentType = res.headers.get("content-type") ?? "";
  const body: unknown = contentType.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    const message = typeof body === "object" && body && "detail" in body
      ? typeof (body as { detail: unknown }).detail === "string"
        ? ((body as { detail: string }).detail)
        : JSON.stringify((body as { detail: unknown }).detail)
      : res.statusText;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  try {
    return await rawRequest<T>(path, opts);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401 && !opts.anonymous && !opts.noRefresh) {
      const refreshed = await useAuthStore.getState().refresh();
      if (refreshed) {
        return await rawRequest<T>(path, opts);
      }
      useAuthStore.getState().clear();
    }
    throw err;
  }
}

// Authenticated binary download. Fetches the file via the bearer header, then
// triggers a browser save dialog using a one-shot object URL. `filename` is
// suggested; the server's Content-Disposition still wins in modern browsers.
export async function apiDownload(path: string, filename: string): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, { headers, credentials: "include" });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(res.status, body || res.statusText, body);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
